import React from "react";
import ServicePageLayout, { H2, H3, P, UL } from "./ServicePageLayout";
import Certifications from "@/components/Certifications";

export const meta = {
  title: "SureCall & WilsonPro Certified Booster Installer Alberta | Epicom Systems",
  metaDescription:
    "SureCall Certified Installer and WilsonPro Certified Installer / Integrator across Southern Alberta. Commercial and residential cell signal booster design and installation — donor-signal survey, engineered antenna placement, and honest advice on when a booster is right vs when you need a DAS.",
  canonicalPath: "/cell-boosters",
  jsonLd: {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://epicom.ca/cell-boosters#service",
    "name": "Cell Signal Booster Installation",
    "serviceType": "Cellular signal booster design and installation",
    "provider": { "@type": "LocalBusiness", "@id": "https://epicom.ca/#business" },
    "areaServed": { "@type": "AdministrativeArea", "name": "Southern Alberta" },
    "description":
      "Cellular signal booster installations for homes, acreages, farms and small commercial buildings across Southern Alberta — engineered around the actual donor signal and building materials. Epicom Systems is a SureCall Certified Installer and a WilsonPro Certified Installer / Integrator.",
    "url": "https://epicom.ca/cell-boosters",
  },
};

export default function CellBoosters() {
  return (
    <ServicePageLayout
      title={meta.title}
      metaDescription={meta.metaDescription}
      canonicalPath={meta.canonicalPath}
      overline="Cell Boosters"
      h1="Cell Phone Signal Boosters — Done Properly"
      intro="A cell booster is a small RF amplifier plus an outdoor donor antenna and one or more indoor antennas. Done well, it turns a marginal outdoor signal into a usable indoor one. Done badly, it turns a marginal outdoor signal into an oscillating mess that gets throttled by the carrier and helps nobody. Epicom installs cellular signal boosters across Southern Alberta the same way we design any RF system: survey the site, understand the donor signal, pick the right gear, mount it correctly."
      jsonLd={meta.jsonLd}
      relatedLinks={[
        { to: "/cellular-das", label: "Cellular DAS (Commercial)" },
        { to: "/point-to-point-wireless", label: "Point-to-Point Wireless" },
        { to: "/southern-alberta", label: "Southern Alberta Service Area" },
      ]}
    >
      <Certifications variant="full" className="!py-10 md:!py-14 !border-0" />

      <H2>Where a booster is the right answer</H2>
      <UL>
        <><strong className="text-white/90">Rural homes and acreages</strong> where the outdoor signal exists but the house itself blocks it — steel roofs, ICF walls, or just distance from the interior of the building to the outside wall.</>
        <><strong className="text-white/90">Farm shops and outbuildings</strong> that need reliable calls and texts for a small crew rather than dozens of simultaneous devices.</>
        <><strong className="text-white/90">Small commercial spaces</strong> — a single-storey shop or office where a right-sized consumer/prosumer booster genuinely solves the problem.</>
        <><strong className="text-white/90">Recreational and vehicle installs</strong> — RVs, work trucks, and mobile setups that need to hold a call while moving through marginal coverage.</>
      </UL>

      <H2>Where a booster is the wrong answer</H2>
      <P>
        Consumer boosters are single-antenna, low-gain, single-user amplifiers. They are not
        rated to cover large commercial buildings, they don’t coordinate multiple indoor
        antennas cleanly, and they can’t handle dozens or hundreds of simultaneous connected
        devices. If the space is a real commercial or industrial facility — deep interior,
        multiple floors, steel construction, or a whole team on multiple carriers — that’s a{" "}
        <a href="/cellular-das" className="text-signal hover:underline">Cellular DAS</a> job,
        not a booster job. Trying to solve a DAS problem with a stack of consumer boosters
        is a common way to waste money and end up with a system that oscillates and gets
        shut down by the carriers.
      </P>

      <H2>What actually determines whether a booster will work</H2>
      <H3>Donor signal</H3>
      <P>
        Boosters amplify what already exists. If the outside signal at the roofline is
        genuinely weak — one bar, deep in a valley, on the back side of a hill — a booster
        can only do so much. Before quoting, we measure the actual outdoor signal at the
        proposed donor antenna location for each carrier that matters to the site. That
        drives everything else.
      </P>
      <H3>Building materials</H3>
      <P>
        Steel roofs, metal siding, ICF, thick masonry, low-E windows — all of these
        attenuate cellular RF, often by a lot. The building envelope is usually the reason
        the interior signal is worse than the exterior signal, and a proper booster install
        works with that fact rather than pretending it doesn’t exist.
      </P>
      <H3>Antenna separation</H3>
      <P>
        The outdoor donor antenna and the indoor antenna(s) need enough physical and RF
        separation to keep the booster from oscillating — feeding its own output back into
        its input. That means real mounting on the roof or a mast, real cable runs, and a
        thought-out interior antenna location. It is not a countertop appliance.
      </P>
      <H3>Cabling and mounting</H3>
      <P>
        Low-loss coax, proper connectors, weatherproofing at every outdoor termination,
        grounding where it belongs. The install lives or dies on the parts most people can’t
        see once the panels are back on.
      </P>

      <H2>Carriers and equipment</H2>
      <P>
        In Canada, the practical carrier list is Rogers / Bell / Telus and their flankers.
        The commercial cellular signal booster systems we install — primarily SureCall and
        WilsonPro, on which we hold current installer certifications — support the bands
        those carriers use. We select the specific model based on the donor signal, the
        size of the space, and the number of indoor antennas the site actually needs.
      </P>

      <H2>SureCall and WilsonPro Certified Installer</H2>
      <P>
        Epicom Systems is a <strong className="text-white/90">SureCall Certified Installer</strong>{" "}
        and a <strong className="text-white/90">WilsonPro Certified Installer / Integrator</strong>.
        Certification means we've been formally trained by the manufacturer on how their
        equipment is engineered, installed, tuned and validated. It does not mean we're
        authorized dealers, exclusive installers, or manufacturer representatives — it
        means when we recommend a specific SureCall or WilsonPro system for your site,
        we've been through the manufacturer's own training on how to make that specific
        product work in the field.
      </P>

      <H3>SureCall cell booster installation</H3>
      <P>
        SureCall's booster line covers a wide range — from small residential and RV/vehicle
        boosters through to commercial-grade multi-antenna systems. As a SureCall Certified
        Installer we deploy the appropriate model for the site: the small stuff where a
        small system genuinely fits, and larger commercial units where the space and user
        count warrant it. Every install starts with a real donor-signal survey before we
        quote — SureCall's own installation practice, not a guess.
      </P>

      <H3>WilsonPro cell booster installation</H3>
      <P>
        WilsonPro is Wilson Electronics' commercial line, aimed at business and enterprise
        coverage rather than consumer use. WilsonPro systems are typically the right choice
        for offices, small industrial spaces, medical and professional buildings, and other
        commercial sites where the boosted coverage has to hold up under multi-user
        workloads. As a WilsonPro Certified Installer / Integrator we design and install
        these systems to the manufacturer's specifications for the space.
      </P>

      <H2>What to expect from an Epicom booster install</H2>
      <UL>
        <>A real signal survey before we quote — no assumed coverage.</>
        <>Honest advice: if a booster isn’t the right fit for the space, we’ll tell you and point at the alternative (usually <a href="/cellular-das" className="text-signal hover:underline">DAS</a> or a{" "}
          <a href="/point-to-point-wireless" className="text-signal hover:underline">wireless bridge</a> to bring service in from a better location).</>
        <>Properly mounted donor antenna with real hardware, not zip-tied to a chimney.</>
        <>Interior antenna placement chosen for the rooms that actually matter to the user.</>
        <>A short walk-through afterwards so you can confirm the coverage where you actually spend time.</>
      </UL>

      <H2>Working with Epicom</H2>
      <P>
        Boosters look simple on the box and are anything but simple in practice. Most of
        our booster jobs are the ones a homeowner or business already tried themselves,
        gave up, and asked us to come sort out. If you’re on that side of the fence — or
        you’d rather skip the DIY experiment entirely — get in touch and we’ll walk through
        what’s actually going to work for your site.
      </P>
    </ServicePageLayout>
  );
}
